import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
	AppBar,
	Toolbar,
	InputBase,
	IconButton,
	Box,
	Tooltip,
	Menu,
	Collapse,
	CircularProgress,
} from "@mui/material";
import {
	Cancel as CancelIcon,
	ExpandLess,
	ExpandMore,
	HelpOutline,
	Lightbulb,
	Menu as MenuIcon,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { usePageContext, type Page } from "../context/PageContext";
import { useAuth } from "../context/AuthContext";
import {
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemButton,
	Typography,
} from "@mui/material";
import { queryData } from "../services/apiServices";

const Search = styled("div")(({ theme }) => ({
	position: "relative",
	borderRadius: theme.shape.borderRadius,
	backgroundColor: alpha(theme.palette.common.white, 0.25),
	"&:hover": {
		backgroundColor: alpha(theme.palette.common.white, 0.25),
	},
	marginLeft: "auto",
	width: "auto",
}));

const ClearIconWrapper = styled(IconButton)(({ theme }) => ({
	position: "absolute",
	right: 0,
	top: "50%",
	transform: "translateY(-50%)",
	padding: theme.spacing(0.9),
	fontSize: "1rem",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
	color: "inherit",
	position: "relative",
	"& .MuiInputBase-input": {
		padding: theme.spacing(1, 1, 1, 0),
		paddingLeft: `calc(1em + ${theme.spacing(0)})`,
		paddingRight: `calc(1em + ${theme.spacing(4)})`,
		transition: theme.transitions.create("width"),
		width: "100%",
		border: `1px solid ${theme.palette.action.disabled}`,
		borderRadius: theme.shape.borderRadius,
		[theme.breakpoints.up("md")]: {
			width: "50ch",
		},
		"&:focus": {
			borderColor: theme.palette.primary.main,
		},
	},
}));

const Header: React.FC = () => {
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<
		{ content: { id: string; ancestors?: { id: string; title: string }[] }; title: string; excerpt: string }[]
	>([]);
	const [showResults, setShowResults] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const { setSelectedPageId, pages } = usePageContext();
	const { isAuthenticated } = useAuth();
	const wrapperRef = useRef(null);
	const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});
	const [selectedIndex, setSelectedIndex] = React.useState(1);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	// Update the value of the same variable in Sidebar component
	const appBarHeight = 64;
	const { headerLogo } = usePageContext();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleToggle = (pageId: string) => {
		setOpenItems((prevState) => ({
			...prevState,
			[pageId]: !prevState[pageId],
		}));
	};

	const renderPages = (pages: Page[], indent = 0) => (
		<List sx={{ flexGrow: 1 }}>
			{pages.map((page) => (
				<React.Fragment key={page.id}>
					<ListItemButton
						onClick={() => {
							const hasChildren = page.children && page.children.length > 0;
							!hasChildren && setSelectedPageId(page.id);
							setSelectedIndex(Number.parseInt(page.id));
							!hasChildren && handleClose();
						}}
						sx={{ ml: indent }}
						selected={selectedIndex === Number.parseInt(page.id)}
					>
						<ListItemText
							primary={page.title}
							onClick={() => {
								page.children &&
									page.children.length > 0 &&
									handleToggle(page.id);
							}}
						/>
						{page.children && page.children.length > 0 && (
							<IconButton onClick={() => handleToggle(page.id)} edge="start">
								{openItems[page.id] ? <ExpandLess /> : <ExpandMore />}
							</IconButton>
						)}
					</ListItemButton>
					{page.children && page.children.length > 0 && (
						<Collapse in={openItems[page.id]} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								{renderPages(page.children, indent + 2)}
							</List>
						</Collapse>
					)}
				</React.Fragment>
			))}
		</List>
	);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout | null = null;

		const performSearch = async () => {
			if (searchQuery.length > 2) {
				try {
					const response = await queryData(isAuthenticated, searchQuery);
					const query = searchQuery.toLowerCase();
					const sorted = [...response].sort((a, b) => {
						const aInTitle = a.title.toLowerCase().includes(query) ? 0 : 1;
						const bInTitle = b.title.toLowerCase().includes(query) ? 0 : 1;
						return aInTitle - bInTitle;
					});
					setSearchResults(sorted);
					setShowResults(true);
				} catch (error) {
					console.error("Error searching:", error);
					setSearchResults([]);
				} finally {
					setIsSearching(false);
				}
			} else {
				setSearchResults([]);
				setIsSearching(false);
			}
		};

		if (searchQuery.trim() !== "") {
			setIsSearching(true);
			setShowResults(false);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			timeoutId = setTimeout(performSearch, 300);
		} else {
			setIsSearching(false);
			setShowResults(false);
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
		};
	}, [searchQuery, isAuthenticated]);

	const handleResultSelect = (pageId: string) => {
		setSearchQuery(""); // Clear the search input
		setSearchResults([]); // Clear search results
		setSelectedPageId(pageId); // Set the selected page ID in the context
	};

	// Handle click outside to close results
	const handleClickOutside = (event: MouseEvent) => {
		if (
			wrapperRef.current &&
			!(wrapperRef.current as any).contains(event.target)
		) {
			setShowResults(false); // Close search results when clicking outside
		}
	};

	function renderHighlighted(text: string): React.ReactNode {
		const segments: React.ReactNode[] = [];
		let remaining = text;
		let key = 0;
		while (remaining.length > 0) {
			const hlStart = remaining.indexOf("@@@hl@@@");
			if (hlStart === -1) { segments.push(remaining); break; }
			if (hlStart > 0) segments.push(remaining.slice(0, hlStart));
			const hlEnd = remaining.indexOf("@@@endhl@@@", hlStart);
			if (hlEnd === -1) { segments.push(remaining.slice(hlStart + 8)); break; }
			segments.push(<mark key={key++}>{remaining.slice(hlStart + 8, hlEnd)}</mark>);
			remaining = remaining.slice(hlEnd + 11);
		}
		return <>{segments}</>;
	}

	function stripHtml(raw: string): string {
		return raw.replace(/<[^>]+>/g, " ").replace(/\s{2,}/g, " ").trim();
	}

	function getBreadcrumb(ancestors?: { id: string; title: string }[]): string {
		if (!ancestors || ancestors.length === 0) return "";
		return ancestors.slice(1).map((a) => a.title).join(" › ");
	}

	const handleClear = () => {
		setSearchQuery("");
		setShowResults(false);
	};

	const navigateToBrainstormSite = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
		window.open(
			"https://rooster.hellonext.co",
			"_blank",
			"noopener noreferrer",
		);
	};

	const navigateToSupportSite = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
		window.open(
			"https://support.roosterinc.com",
			"_blank",
			"noopener noreferrer",
		);
	};

	// Attach the event listener to detect outside clicks
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			setSearchQuery("");
			setShowResults(false);
		};
	}, []);

	return (
		<AppBar
			position="fixed"
			sx={{
				backgroundColor: "white",
				color: "black",
				zIndex: (theme) => theme.zIndex.drawer + 1,
				height: appBarHeight,
				"@media print": {
					display: "none",
				},
			}}
			variant="outlined"
			ref={wrapperRef}
			elevation={0}
		>
			<Toolbar>
				{isMobile ? (
					<img
						src={headerLogo ? headerLogo : "/Rooster Logo (small).png"}
						alt="Logo"
						style={{ width: "30px" }}
					/>
				) : (
					<img
						src={headerLogo ? headerLogo : "/rooster logo.png"}
						alt="Logo"
						style={{ width: "150px" }}
					/>
				)}
				{/* <img src={headerLogo ? headerLogo : "/rooster logo.png"} alt="Logo" /> */}
				<Box sx={{ flexGrow: 1 }} />
				{isAuthenticated && (
					<Tooltip title="Submit a Support Ticket">
						<IconButton sx={{ mr: 1 }} onClick={navigateToSupportSite}>
							<HelpOutline />
						</IconButton>
					</Tooltip>
				)}
				{isAuthenticated && (
					<Tooltip title="Submit a brainstorm">
						<IconButton sx={{ mr: 1 }} onClick={navigateToBrainstormSite}>
							<Lightbulb />
						</IconButton>
					</Tooltip>
				)}
				<Search>
					<StyledInputBase
						placeholder="Search..."
						inputProps={{ "aria-label": "search" }}
						value={searchQuery}
						onChange={handleSearchInputChange}
					/>

					{searchQuery && (
						<ClearIconWrapper onClick={handleClear}>
							<CancelIcon sx={{ fontSize: 20 }} />
						</ClearIconWrapper>
					)}

					{isSearching && !showResults && searchQuery.length > 2 && (
						<Paper
							sx={{
								position: "absolute",
								top: "100%",
								left: 0,
								right: 0,
								zIndex: 1,
								borderRadius: 0,
								p: 3,
								display: "flex",
								justifyContent: "center",
							}}
						>
							<CircularProgress size={24} />
						</Paper>
					)}

					{showResults && searchResults.length === 0 && !isSearching && searchQuery.length > 2 && (
						<Paper
							sx={{
								position: "absolute",
								top: "100%",
								left: 0,
								right: 0,
								zIndex: 1,
								borderRadius: 0,
								p: 3,
								textAlign: "center",
							}}
						>
							<Typography variant="body2" color="text.secondary">
								No results found for "{searchQuery}"
							</Typography>
						</Paper>
					)}

					{showResults && searchResults.length > 0 && (
						<Paper
							sx={{
								position: "absolute",
								top: "100%",
								left: 0,
								right: 0,
								zIndex: 1,
								overflowY: "auto",
								borderRadius: 0,
								height: "calc(100vh - 160px)",
							}}
						>
							<Box>
								<List>
									{searchResults.map((result) => {
										const breadcrumb = getBreadcrumb(result.content.ancestors);
										const cleanExcerpt = result.excerpt ? stripHtml(result.excerpt) : "";
										return (
											<ListItem key={result.content.id} disablePadding>
												<ListItemButton
													onClick={() => handleResultSelect(result.content.id)}
												>
													<ListItemText
														primary={renderHighlighted(result.title)}
														secondary={
															<>
																{breadcrumb && (
																	<Typography
																		component="span"
																		variant="caption"
																		color="text.disabled"
																		display="block"
																		sx={{ mb: 0.25 }}
																	>
																		{breadcrumb}
																	</Typography>
																)}
																{cleanExcerpt && renderHighlighted(cleanExcerpt)}
															</>
														}
													/>
												</ListItemButton>
											</ListItem>
										);
									})}
								</List>
							</Box>
						</Paper>
					)}
				</Search>
				{isMobile && (
					<IconButton sx={{ ml: { xs: 6 } }} onClick={handleClick}>
						<MenuIcon />
					</IconButton>
				)}
			</Toolbar>
			{isMobile && (
				<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
					{renderPages(pages)}
				</Menu>
			)}
		</AppBar>
	);
};

export default Header;
